/**
 * Surface elevation system — types and class maps.
 *
 * Levels 1–8 mirror the Fluid Functionalism spec
 * (https://www.fluidfunctionalism.com/docs/surfaces). The CSS tokens live in
 * `app/styles/surfaces.css`; this file is the TypeScript-side contract for
 * picking a level and translating it into the `bg-surface-N` /
 * `shadow-surface-N` Tailwind utilities registered in `theme-inline.css`.
 *
 * Static maps (not template strings) so Tailwind's source scanner can see
 * every class name literally at build time.
 */

export type SurfaceLevel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** The lowest level — page substrate. The default context value. */
export const SURFACE_BASE_LEVEL: SurfaceLevel = 1;
/** The highest level — nothing can sit above this. Clamps offsets. */
export const SURFACE_MAX_LEVEL: SurfaceLevel = 8;

export const SURFACE_BG_CLASSES: Record<SurfaceLevel, string> = {
	1: "bg-surface-1",
	2: "bg-surface-2",
	3: "bg-surface-3",
	4: "bg-surface-4",
	5: "bg-surface-5",
	6: "bg-surface-6",
	7: "bg-surface-7",
	8: "bg-surface-8",
};

export const SURFACE_SHADOW_CLASSES: Record<SurfaceLevel, string> = {
	1: "shadow-surface-1",
	2: "shadow-surface-2",
	3: "shadow-surface-3",
	4: "shadow-surface-4",
	5: "shadow-surface-5",
	6: "shadow-surface-6",
	7: "shadow-surface-7",
	8: "shadow-surface-8",
};

/**
 * Clamp an arbitrary level to the [1, 8] range. Use after adding an offset
 * to the current substrate so popovers nested inside the deepest dialog
 * still render at a valid level (capped at 8) instead of producing a
 * `bg-surface-12` class that doesn't exist.
 */
export function clampSurfaceLevel(level: number): SurfaceLevel {
	if (level <= SURFACE_BASE_LEVEL) {
		return SURFACE_BASE_LEVEL;
	}
	if (level >= SURFACE_MAX_LEVEL) {
		return SURFACE_MAX_LEVEL;
	}
	return level as SurfaceLevel;
}
